import React,{useEffect,useState} from 'react';
import {FiAlertTriangle,FiInfo,FiTrash2,FiX} from 'react-icons/fi';
import './ProjectDeleteDialog.css';

export function formatStorageBytes(bytes){
 const value=Math.max(0,Number(bytes)||0);
 if(value===0)return '0 MB';
 if(value>=1e9)return (value/1e9).toFixed(2)+' GB';
 if(value>=1e6)return (value/1e6).toFixed(1)+' MB';
 return (value/1e3).toFixed(1)+' KB';
}

export default function ProjectDeleteDialog({project,onPreview,onConfirm,onClose,mode='trash'}){
 const toTrash=mode==='trash';
 const [preview,setPreview]=useState(null);
 const [loading,setLoading]=useState(true);
 const [working,setWorking]=useState(false);
 const [error,setError]=useState('');
 const [deleteMedia,setDeleteMedia]=useState(false);

 useEffect(()=>{
  let live=true;
  setLoading(true);setPreview(null);setDeleteMedia(false);setError('');
  if(toTrash){setPreview({files:[],reclaim_bytes:0});setLoading(false);return()=>{live=false;};}
  onPreview(project.id).then(result=>{
   if(live){setPreview(result);setLoading(false);}
  }).catch(err=>{
   if(live){setError(err.message||'Could not check which files are shared.');setLoading(false);}
  });
  return()=>{live=false;};
 },[project.id,toTrash,onPreview]);

 const files=preview?.files||[];
 const retained=files.filter(file=>file.eligible||file.shared);
 const deletable=files.filter(file=>file.eligible);
 const shared=files.filter(file=>file.shared);
 const count=deletable.length;
 const space=formatStorageBytes(preview?.reclaim_bytes);
 const submit=async()=>{
  if(!preview||working)return;
  if(deleteMedia&&count>0&&!window.confirm(
   `Permanently delete ${count} interview file${count===1?'':'s'} from Media Library?\n\n${space} of cloud storage will be reclaimed after deletion completes. This cannot be undone. Shared files will not be deleted.`
  ))return;
  setWorking(true);setError('');
  try{
   const ok=await onConfirm(project,{deleteMedia:!toTrash&&deleteMedia&&count>0,mode:toTrash?'trash':'permanent'});
   if(!ok)setError('Deletion did not complete. Your project may still be available. Please refresh and check.');
  }catch(err){setError(err.message||'Could not delete this project.');}
  finally{setWorking(false);}
 };
 return <div className="modal-backdrop project-delete-overlay" role="presentation">
  <section className="modal project-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="project-delete-title" aria-describedby="project-delete-description">
   <button type="button" className="project-delete-close" aria-label="Cancel deletion" disabled={working} onClick={onClose}><FiX/></button>
   <div className="project-delete-title"><FiTrash2 aria-hidden="true"/><h2 id="project-delete-title">{toTrash?"Move project to Trash?":"Delete permanently?"}</h2></div>
   <strong className="project-delete-name">{preview?.project_name||project.data?.projectName||project.data?.title||'Untitled project'}</strong>
   <p id="project-delete-description" className="project-delete-description">
    {toTrash?'This project and all its interviews, Assembly, comments and history will move to Trash. You can restore everything later.':'This permanently removes the project and its interviews, Assembly, feedback, settings and edit history. This cannot be undone.'}
   </p>
   {toTrash&&<div className="project-delete-consequences" role="status">
     <FiInfo aria-hidden="true"/><div>
      <strong>Your work remains recoverable in Trash.</strong>
      <span>Uploaded video files remain in Media Library. Moving to Trash does not free storage and does not delete transcripts, comments or Assembly history.</span>
     </div>
    </div>}
   {loading?<p role="status" className="project-delete-loading">Checking interview files and storage…</p>
    : preview && !toTrash && <>
     <label className={`project-delete-choice${count===0?' is-unavailable':''}`}>
      <input type="checkbox" checked={deleteMedia} disabled={working||count===0} onChange={event=>setDeleteMedia(event.target.checked)}/>
      <span><strong>Also permanently delete this project's interview files</strong>
       <small>Remove eligible uploaded video files and saved transcripts from Media Library.</small>
      </span>
     </label>
     {deleteMedia ? <div className="project-delete-consequences is-destructive" role="status">
      <FiAlertTriangle aria-hidden="true"/>
      <div><strong>{count} interview file{count===1?'':'s'} will be permanently deleted.</strong>
       <span>They will no longer be available in Media Library and cannot be recovered.</span>
       <strong className="project-delete-space">{space} of cloud storage will be reclaimed.</strong>
       {shared.length>0&&<span>{shared.length} shared file{shared.length===1?' is':'s are'} used by other projects and will be kept.</span>}
      </div>
     </div> : <div className="project-delete-consequences" role="status">
      <FiInfo aria-hidden="true"/>
      <div><strong>Interview files will not be deleted.</strong>
       <span>{retained.length} file{retained.length===1?' stays':'s stay'} available in your Media Library for reuse. No media storage will be reclaimed.</span>
       {count===0&&<span>No files are eligible for permanent deletion{shared.length?' because they are shared with other projects':''}.</span>}
      </div>
     </div>}
     {deleteMedia && files.length>0&&<div className="project-delete-filelist">
      <strong>File-by-file impact</strong>
      <ul>{files.map(file=><li key={file.path}>
       <span title={file.name}>{file.name}</span>
       <small>{file.eligible?`Delete · ${formatStorageBytes(file.bytes)}`:file.shared?'Keep · Used by another project':'Keep · Not eligible'}</small>
      </li>)}</ul>
     </div>}
    </>}
   {error&&<p className="project-delete-error" role="alert">{error}</p>}
   <div className="project-delete-actions">
    <button type="button" className="project-delete-cancel" onClick={onClose} disabled={working}>Cancel</button>
    <button type="button" className="project-delete-submit" onClick={submit} disabled={working||loading||!preview}>
     <FiTrash2 aria-hidden="true"/> {working?(toTrash?'Moving…':'Deleting…'):toTrash?'Move to Trash':deleteMedia?`Delete project + ${count} file${count===1?'':'s'}`:'Delete permanently'}
    </button>
   </div>
  </section>
 </div>;
}
